import { css } from "@emotion/react"
import styled from "@emotion/styled"
import MuiTab from "@mui/material/Tab"
import MuiTabs from "@mui/material/Tabs"
import Typography from "@mui/material/Typography"
import { Link } from "utils"
import {
  HEADER_HEIGHT_DESKTOP,
  HEADER_HEIGHT_MOBILE,
} from "../../layout/Header/styles"
import { colors } from "../../theme/colors"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"

export const Root = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[32]}px;
  padding: ${sizes.spacing[48]}px 0;
  scroll-margin-top: ${HEADER_HEIGHT_MOBILE + 16}px;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[48]}px;
    padding: ${sizes.spacing[64]}px 0;
    scroll-margin-top: ${HEADER_HEIGHT_DESKTOP + 16}px;
  }

  .MuiTabs-root {
    width: 100%;
    max-width: 100%;
  }
`

export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
  /* EGDE-384: pagination scrolls here; offset clears the compact scroll-pill
     header (fixed at top:16 + ~64px tall) so the title lands just below it. */
  scroll-margin-top: 76px;
`

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
`

export const Title = styled(Typography)`
  color: ${colors.primitives.neutral600};
`

export const ChevronWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

export const Subtitle = styled(Typography)`
  color: ${colors.primitives.neutral500};
  text-align: start;
  width: 100%;
`

export const FilterTabs = styled(MuiTabs)`
  width: 100%;
  min-height: 0;

  .MuiTabs-indicator {
    display: none;
  }

  .MuiTabs-flexContainer {
    gap: 0;
  }
`

export const FilterTab = styled(MuiTab)`
  && {
    min-width: 120px;
    min-height: 0;
    padding: 0 ${sizes.spacing[16]}px ${sizes.spacing[8]}px;
    text-transform: none;
    font-size: 20px;
    font-weight: 500;
    line-height: 1.4;
    color: ${colors.primitives.neutral600};
    opacity: 1;
    border-bottom: 1px solid ${colors.primitives.neutral300};
    transition: border-bottom-color 0.2s ease;

    /* EGDE-496: tab text is already black at rest, so hover darkens the
       underline instead — same treatment as the pagination dots. */
    &:hover:not(.Mui-selected) {
      border-bottom-color: ${colors.primitives.neutral500};
    }

    &.Mui-selected {
      font-weight: 700;
      color: ${colors.primitives.neutral600};
      border-bottom: 2px solid ${colors.primitives.warning500};
    }
  }
`

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${sizes.spacing[24]}px;
  width: 100%;
  /* EGDE-384: fallback scroll target (title-less lobby); same pill offset. */
  scroll-margin-top: 96px;

  @media (min-width: ${breakpoints.lg}px) {
    grid-template-columns: 1fr 1fr 1fr;
    gap: ${sizes.spacing[24]}px;
  }
`

/* EGDE-385: opt-in mobile carousel (matches the magazine lobby). */
export const SwiperWrapper = styled.div`
  width: 100%;
  overflow: hidden;

  .swiper {
    width: 100%;
    overflow: visible;
  }

  .swiper-slide {
    height: auto;
  }
`

const cardBase = css`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
  padding: ${sizes.spacing[16]}px;
  border: 1px solid ${colors.primitives.neutral300};
  border-radius: ${sizes.borderRadius[24]}px;
  background-color: ${colors.light.bg.primary};

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[24]}px;
  }
`

export const CardLink = styled(Link)`
  ${cardBase};
  text-decoration: none;
  cursor: pointer;
  transition: border-color 150ms ease-in-out;

  &:hover {
    border-color: ${colors.primitives.neutral500};
  }
`

/* EGDE-578: a card whose CMS item has no link renders as a plain box — same
   visuals, but no hover affordance and no pointer cursor. */
export const CardStatic = styled.div`
  ${cardBase};
`

export const CardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 352 / 220;
  border-radius: ${sizes.borderRadius[24]}px;
  overflow: hidden;

  img {
    transition: transform 0.3s ease-in-out;
  }

  ${CardLink}:hover & img {
    transform: scale(1.05);
  }
`

export const CategoryBadge = styled.div`
  position: absolute;
  inset-inline-start: ${sizes.spacing[16]}px;
  bottom: ${sizes.spacing[16]}px;
  background-color: rgba(255, 255, 255, 0.65);
  border-radius: ${sizes.borderRadius.full}px;
  padding: ${sizes.spacing[6]}px ${sizes.spacing[12]}px;
  backdrop-filter: blur(4px);
`

export const BadgeText = styled(Typography)`
  color: ${colors.primitives.neutral600};
  font-weight: 700 !important;
`

export const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
  flex: 1;
`

export const CardHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
`

export const CardTitle = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
  text-align: start;
`

export const CardDescription = styled(Typography)`
  color: ${colors.primitives.neutral500};
  text-align: start;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: auto;
`

export const CtaWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[6]}px;
  width: 100%;
  min-width: 0;
`

export const CtaText = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
  /* EGDE-381: long read labels must wrap onto multiple rows instead of
     stretching the card on one line. */
  min-width: 0;
  overflow-wrap: anywhere;
`

export const CtaArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${colors.primitives.primary500};

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  width: 100%;
  color: ${colors.primitives.neutral500};
`

export const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${sizes.spacing[16]}px;
  width: 100%;

  /* EGDE-386: desktop pagination aligns to the start (right in RTL)
     edge of the cards container; mobile stays centered */
  @media (min-width: ${breakpoints.lg}px) {
    justify-content: flex-start;
  }
`

export const PaginationArrow = styled.button`
  /* EGDE-496 r2: 32px like the FAQ pagination — the 20px hover disc hugged
     the glyph and read as "too small". */
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  border-radius: ${sizes.borderRadius.full}px;
  color: ${colors.primitives.neutral600};
  transition: background-color 0.2s ease;

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  /* EGDE-496: the glyph is already black at rest, so hover paints the
     neutral200 disc behind it instead (neutral100 is indistinguishable from
     white). */
  &:hover:not(:disabled) {
    background-color: ${colors.primitives.neutral200};
  }
`

export const PageButton = styled.button<{ $active: boolean }>`
  width: 32px;
  height: 32px;
  /* EGDE-433: never let the flex row squash the circles into ovals. */
  flex-shrink: 0;
  border-radius: ${sizes.borderRadius.full}px;
  border: 1px solid
    ${(p) =>
      p.$active ? colors.primitives.warning500 : colors.primitives.neutral300};
  background-color: ${(p) =>
    p.$active ? colors.primitives.secondary500 : colors.primitives.neutral0};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition:
    background-color 150ms ease-in-out,
    border-color 150ms ease-in-out,
    color 150ms ease-in-out;

  &:hover:not(:disabled) {
    border-color: ${colors.primitives.neutral500};
  }
`

export const PageButtonText = styled.span<{ $active: boolean }>`
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  color: ${(p) =>
    p.$active ? colors.primitives.neutral600 : colors.primitives.neutral500};
`
